import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const frontmatter = {
  "title": "From DesignOps to AgentOps",
  "slug": "from-designops-to-agentops",
  "publishedAt": "2026-06-19T08:00:00.000Z",
  "readTime": "8 min read",
  "excerpt": "As AI agents enter product delivery, design-system leaders become operators of context, contracts, review loops, and trust.",
  "authorName": "Petri Lahdelma",
  "authorSlug": "petri-lahdelma",
  "status": "scheduled",
  "series": "Agentic Design Systems",
  "tags": ["DesignOps", "AgentOps", "Design Leadership", "AI"],
  "mainImageUrl": "/blog/agentic-design-systems/hero-05-agentops.png",
  "mainImageAlt": "Bridge from DesignOps through context operator to AgentOps"
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    h2: "h2",
    hr: "hr",
    p: "p",
    strong: "strong",
    ..._provideComponents(),
    ...props.components
  }, {ArticleFigure} = _components;
  if (!ArticleFigure) _missingMdxReference("ArticleFigure", true);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "DesignOps solved a human coordination problem: how design teams work with clarity, consistency, and organizational trust across rituals, libraries, critiques, and handoffs to engineering. That work still matters. The environment changed."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Thesis:"
      }), " Agents extend DesignOps; they do not replace it. DesignOps made collaboration legible. AgentOps makes automated collaboration inspectable. If you lead a design system, you are the natural owner of the context, contracts, and review loops agents inherit — whether or not your title changes."]
    }), "\n", _jsxs(_components.p, {
      children: ["Agents now show up as implementers, reviewers, test writers, documentation assistants, and sometimes autonomous editors in the repo. I use ", _jsx(_components.strong, {
        children: "AgentOps"
      }), " as internal vocabulary — not a new department, but DesignOps with agent guardrails: automated work reliable enough to use on real product systems."]
    }), "\n", _jsxs(_components.p, {
      children: ["This post closes the series on ", _jsx(_components.a, {
        href: "/blog/agentic-design-systems-need-operating-models-not-more-components",
        children: "operating models"
      }), ", ", _jsx(_components.a, {
        href: "/blog/tools-harnesses-and-skills-the-missing-model-for-design-systems",
        children: "tools and harnesses"
      }), ", ", _jsx(_components.a, {
        href: "/blog/component-contracts-are-where-taste-becomes-infrastructure",
        children: "contracts"
      }), ", and a ", _jsx(_components.a, {
        href: "/blog/the-minimum-viable-agent-ready-design-system",
        children: "minimum viable stack"
      }), "."]
    }), "\n", _jsxs(ArticleFigure, {
      layout: "full",
      children: [_jsx("img", {
        src: "/blog/agentic-design-systems/fig-agentops-bridge.png",
        alt: "Bridge from DesignOps coordination to AgentOps with inspectable agent guardrails."
      }), _jsx("figcaption", {
        children: "DesignOps made collaboration legible; AgentOps makes automated collaboration inspectable."
      })]
    }), "\n", _jsx(_components.h2, {
      children: "What DesignOps already solved"
    }), "\n", _jsx(_components.p, {
      children: "DesignOps gave teams shared vocabulary for critique, intake, and release. It aligned Figma libraries with roadmaps, defined who approves patterns, and made handoffs legible to engineering. Those problems do not disappear because an agent can open a PR. If anything, handoffs multiply — human to human, human to agent, agent to CI, CI back to human."
    }), "\n", _jsx(_components.p, {
      children: "The mistake is treating AgentOps as a replacement discipline. It is an extension. You still need critique culture. You still need adoption politics. You still need a human who can say “this is on-brand but wrong for this moment.” AgentOps adds mechanical gates so that conversation happens at the right altitude."
    }), "\n", _jsx(_components.h2, {
      children: "The design-system lead becomes a context operator"
    }), "\n", _jsx(_components.p, {
      children: "In classic design-system work, context lived in people — why a component exists, why a pattern failed last quarter, which API is legacy-only. Agents need that context written, structured, and retrievable. The expanded job is curating the information environment: which documents are authoritative, which examples agents should copy, which patterns are safe for generation, which rules are hard constraints versus guidance, and which decisions still require human taste."
    }), "\n", _jsxs(_components.p, {
      children: ["Context operators do unglamorous work. They delete duplicate stories. They mark deprecated variants in contracts. They move “do not copy” examples behind clearer names. They ensure ", _jsx(_components.code, {
        children: "AGENTS.md"
      }), " points to the current harness, not a doc from two reorganizations ago. Bad context produces bad automation at scale. Good context compounds."]
    }), "\n", _jsx(_components.p, {
      children: "A practical test: start a fresh agent session on a real ticket. Time how long until it edits the right files with the right examples. If the answer is “it cannot without a human paste,” your context layer is still personal, not operational."
    }), "\n", _jsx(_components.h2, {
      children: "AgentOps is not “let the AI do it”"
    }), "\n", _jsx(_components.p, {
      children: "The immature version asks for a screen, receives a screen, merges it, and discovers problems in QA. AgentOps asks for inspectability: rules known before editing, source of truth explicit, file boundaries enforced, validation run before review, mechanical changes separated from judgment. If an agent changes a component, the team should see why, against which contract, with which tests, under which review rule."
    }), "\n", _jsxs(_components.p, {
      children: ["We do not run a numeric eval pipeline yet. We do run mechanical gates. Example: an agent-assisted PR renamed a Storybook story without updating ", _jsx(_components.code, {
        children: "Button.contract.json"
      }), ". ", _jsx(_components.code, {
        children: "npm run validate:components -- --component Button"
      }), " failed in CI — required story missing, drift caught before a human opened the diff. That is AgentOps at v1: failing commands as evaluation, not Slack vibes or fabricated scores."]
    }), "\n", _jsx(_components.p, {
      children: "Later maturity adds targeted evals — golden flows, forbidden composition checks, regression suites on pattern recipes. The principle stays: evidence before merge, not vibes after demo."
    }), "\n", _jsx(_components.h2, {
      children: "Responsibilities that extend the role"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Contract design"
      }), " turns repeatable quality into rules. When the same review comment appears three times, it becomes a contract field or pattern rule."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Context curation"
      }), " promotes the right examples and demotes stale ones. Storybook is not a gallery; it is training data."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Workflow design"
      }), " sets harness boundaries for common agent tasks — component edits, story additions, token changes, doc updates — each with different allowed paths."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Evaluation"
      }), " turns repeat failures into rules instead of repeat review comments. CI failures are feedback. Track them."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Escalation design"
      }), " keeps brand, legal copy, destructive flows, and accessibility exceptions on the human side. Agents should know when to stop and ask."]
    }), "\n", _jsx(_components.p, {
      children: "Design-system practitioners already translate between product intent, brand, accessibility, APIs, and adoption politics. Agents amplify whichever system you actually have — coherent or vague."
    }), "\n", _jsx(_components.h2, {
      children: "When AgentOps is not worth the tax"
    }), "\n", _jsxs(_components.p, {
      children: ["Defer when agents are not in your delivery loop, when you cannot maintain contracts after launch, or when a strong ", _jsx(_components.code, {
        children: "AGENTS.md"
      }), " and Storybook cover most of your risk for the next two quarters. Maintenance is real: contracts only help if someone updates them when props change. Engineering may route agents around the design system unless the harness lives in the repo they use."]
    }), "\n", _jsx(_components.p, {
      children: "If your organization bans agents in production repos but allows them in prototypes, invest in DesignOps as before. AgentOps activates when automated edits hit shared code."
    }), "\n", _jsx(_components.h2, {
      children: "A cadence that fits a working squad"
    }), "\n", _jsx(_components.p, {
      children: "Treat this as a learning loop, not a ceremony program."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Daily:"
      }), " Did agent-assisted work pass the harness? If not, was it a scope miss, a missing example, or a missing rule?"]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Weekly:"
      }), " Which failures repeated? Tag them: tool, harness, skill, contract, human judgment."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Monthly:"
      }), " Promote repeats into contracts, examples, or skills. Remove one stale story agents keep finding."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Quarterly:"
      }), " Is the system safer than last quarter? Compare review time on mechanical vs judgment comments. Survey contributors — human and otherwise — on whether they know which example is canonical."]
    }), "\n", _jsx(_components.h2, {
      children: "Bridging design leadership and engineering trust"
    }), "\n", _jsx(_components.p, {
      children: "AgentOps fails when design owns rules engineering will not enforce, or engineering wires agents that bypass design context. The bridge is shared evidence: contracts in the repo, validators in CI, examples both sides recognize. Design-system leads do not need to write TypeScript; they need to show up when validation fails for product reasons, not treat CI as someone else’s department."
    }), "\n", _jsx(_components.p, {
      children: "When engineering proposes “let the agent refactor tokens globally,” the context operator asks for scope, rollback, and verification — the same questions you would ask a junior engineer with root access. Trust compounds when agents fail safely in preview, not when they ship surprises on Friday."
    }), "\n", _jsx(_components.p, {
      children: "Leadership visibility matters: one slide per quarter showing loop closure — rules added, evidence upgraded, repeat failures eliminated — beats a vanity metric on components shipped. AgentOps is measured in fewer repeat mistakes, not more agent demos."
    }), "\n", _jsx(_components.p, {
      children: "Your title may still say DesignOps. Your calendar should reflect context operation — curating examples, reviewing validator failures, converting critique into contracts. That is the job agents amplified, not replaced."
    }), "\n", _jsx(_components.p, {
      children: "If the same mistake appears three times, it should stop being a review comment and become a rule, an example, or a check. That sentence is the whole series in one line."
    }), "\n", _jsx(_components.h2, {
      children: "Closing"
    }), "\n", _jsxs(_components.p, {
      children: ["Craft moves upstream. Instead of only designing the button, you design the rules that keep the next thousand decisions coherent. Teams will use agents. The question is whether work happens inside the ", _jsx(_components.a, {
        href: "/blog/agentic-design-systems-need-operating-models-not-more-components",
        children: "operating model"
      }), " or around it — with context, evidence, and trust designed as deliberately as the components themselves."]
    }), "\n", _jsx(_components.p, {
      children: "You already have the spine: intent, rules, output, evidence, better rules. Tools expose surfaces. Harnesses enforce boundaries. Skills package repeat work. Contracts turn taste into infrastructure. The minimum stack keeps it maintainable. AgentOps keeps it yours to operate."
    }), "\n", _jsx(_components.h2, {
      children: "What to do Monday morning"
    }), "\n", _jsx(_components.p, {
      children: "If you only do three things after this series: assign an owner for contracts and context, wire one validation command into CI for your top five components, and run the harness audit from Part 2. Everything else — semantic rules, skills, evals — compounds from there."
    }), "\n", _jsx(_components.p, {
      children: "The goal was never “AI in the design system.” The goal is a design system that still makes sense when agents are in the repo — operated, evidenced, and honest about what fails today versus what you aspire to enforce tomorrow."
    }), "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Series:"
      }), " Part 5 of 5 · ", _jsx(_components.strong, {
        children: "←"
      }), " ", _jsx(_components.a, {
        href: "/blog/the-minimum-viable-agent-ready-design-system",
        children: "Minimum Viable Agent-Ready"
      }), " · ", _jsx(_components.strong, {
        children: "Start →"
      }), " ", _jsx(_components.a, {
        href: "/blog/agentic-design-systems-need-operating-models-not-more-components",
        children: "Operating Models"
      })]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
